import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Tooltip,
  Card,
  } from "@mui/material";
import {
  Storage as StorageIcon,
  FolderOpen as FolderOpenIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { DownloadButton } from "../shared/DownloadButton";
import { isImageFile } from "../../hooks/useFileOperations";

export const DrawerContent = ({
  currentFolder,
  folderLoading,
  error,
  onFolderClick,
  renderFileIconOrThumbnail,
  renderImageThumbnail,
  selectedThumbnailFile,
  highlightPath,
  hideEmptyFolders,
}: any) => {
  const highlightRef = useRef<any>(null);

  useEffect(() => {
    if (highlightPath && highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightPath, currentFolder]);

  const formatBytes = (bytes: any) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const renderFilePreview = (file: any) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* File Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {renderFileIconOrThumbnail(file)}
            <Typography variant="h6" component="h2">
              {file.name || "Unnamed File"}
            </Typography>
          </Box>
          <Chip label="File" size="small" color="info" variant="outlined" />
        </Box>

        {/* File Details */}
        <List disablePadding>
          {file.path && (
            <ListItem divider>
              <ListItemIcon>
                <FolderOpenIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Path"
                secondary={file.path}
                secondaryTypographyProps={{ sx: { wordBreak: "break-all" } }}
              />
            </ListItem>
          )}

          {file.size && (
            <ListItem divider>
              <ListItemIcon>
                <StorageIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="Size" secondary={formatBytes(file.size)} />
            </ListItem>
          )}

          {file.servermodified && (
            <ListItem divider>
              <ListItemIcon>
                <CalendarIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Server Modified"
                secondary={new Date(file.servermodified).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              />
            </ListItem>
          )}

          {file.clientmodified && (
            <ListItem divider>
              <ListItemIcon>
                <CalendarIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Client Modified"
                secondary={new Date(file.clientmodified).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              />
            </ListItem>
          )}

          {file.isdownloadable !== undefined && (
            <ListItem divider>
              <ListItemText
                primary="Downloadable"
                secondary={file.isdownloadable ? "Yes" : "No"}
              />
            </ListItem>
          )}

          {file.isshared !== undefined && (
            <ListItem divider>
              <ListItemText
                primary="Shared"
                secondary={file.isshared ? "Yes" : "No"}
              />
            </ListItem>
          )}
        </List>

        {/* Additional Properties */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Additional Properties:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.entries(file)
              .filter(
                ([key, value]) =>
                  ![
                    "id",
                    "name",
                    "path",
                    "type",
                    "size",
                    "servermodified",
                    "clientmodified",
                    "isdownloadable",
                    "isshared",
                    "children",
                  ].includes(key) &&
                  value !== null &&
                  value !== undefined &&
                  value !== "" &&
                  typeof value !== "object",
              )
              .map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${String(value)}`}
                  size="small"
                  variant="outlined"
                  color="default"
                />
              ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderFolderContent = () => {
    if (!currentFolder) return null;

    if (currentFolder.type === "file") {
      return renderFilePreview(currentFolder);
    }

    if (!currentFolder.children || currentFolder.children.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          This folder is empty.
        </Alert>
      );
    }

    // Deep check: a folder is empty if it has no files anywhere in its subtree.
    // Recursively walks children — returns true if no file nodes are found.
    const isDeepEmptyFolder = (node: any): boolean => {
      if (node.type === "file") return false;
      const children = node.children;
      if (!children || !Array.isArray(children) || children.length === 0) return true;
      for (const child of children) {
        if (!isDeepEmptyFolder(child)) return false;
      }
      return true;
    };

    // Separate children into folders, non-image files, and images
    const folders = currentFolder.children
      .filter(
        (child: any) =>
          child.type === "folder" &&
          !(hideEmptyFolders && isDeepEmptyFolder(child))
      )
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));

    const nonImageFiles = currentFolder.children
      .filter((child: any) => child.type === "file" && !isImageFile(child.name))
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));

    const imageFiles = currentFolder.children
      .filter((child: any) => child.type === "file" && isImageFile(child.name))
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));

    // After filtering, if nothing is left to show, treat the folder as empty
    if (folders.length === 0 && nonImageFiles.length === 0 && imageFiles.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          This folder is empty.
        </Alert>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Folders and Non-Image Files */}
        {(folders.length > 0 || nonImageFiles.length > 0) && (
          <List disablePadding>
            {[...folders, ...nonImageFiles].map((child: any, index) => {
              const isSelected =
                selectedThumbnailFile &&
                selectedThumbnailFile.path === child.path;
              const isHighlighted =
                highlightPath &&
                (child.path || child.path_display || "").toLowerCase() ===
                  highlightPath.toLowerCase();
              return (
                <React.Fragment key={child.id || index}>
                  <ListItem
                    disablePadding
                    ref={isHighlighted ? highlightRef : null}
                  >
                    <ListItemButton
                      sx={{
                        borderRadius: 0,
                        gap: 2,
                        bgcolor: isHighlighted
                          ? "rgba(46, 125, 50, 0.12)"
                          : isSelected
                            ? "rgba(25, 118, 210, 0.08)"
                            : "transparent",
                        border: isHighlighted
                          ? "2px solid rgba(46, 125, 50, 0.5)"
                          : "2px solid transparent",
                        "&:hover": {
                          bgcolor: isHighlighted
                            ? "rgba(46, 125, 50, 0.18)"
                            : isSelected
                              ? "rgba(25, 118, 210, 0.12)"
                              : "action.hover",
                        },
                        height: "100%",
                      }}
                      onClick={() => onFolderClick(child)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: "64px",
                          minHeight: "64px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {renderFileIconOrThumbnail(child)}
                      </ListItemIcon>
                      <ListItemText primary={child.name || "Unnamed"} />
                      <Box>
                        {child.type === "folder" && (
                          <Chip
                            label={
                              child.children && child.children.length > 0
                                ? `${child.children.length} items`
                                : "Empty"
                            }
                            size="small"
                            color={
                              child.children && child.children.length > 0
                                ? "default"
                                : "secondary"
                            }
                            variant="outlined"
                          />
                        )}
                        {child.type === "file" && (
                          <DownloadButton file={child} sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}

        {/* Images Grid */}
        {imageFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Images ({imageFiles.length})
            </Typography>
            <Grid container spacing={1}>
              {imageFiles.map((imageFile: any, index: any) => {
                const isSelected =
                  selectedThumbnailFile &&
                  selectedThumbnailFile.path === imageFile.path;
                const isHighlighted =
                  highlightPath &&
                  (
                    imageFile.path ||
                    imageFile.path_display ||
                    ""
                  ).toLowerCase() === highlightPath.toLowerCase();

                return (
                  <Grid size={{ xs: 4 }} key={imageFile.id || index}>
                    <Tooltip title={imageFile.name} placement="top">
                      <Card
                        sx={{
                          cursor: "pointer",
                          border: isHighlighted
                            ? "2px solid rgba(46, 125, 50, 0.7)"
                            : isSelected
                              ? "2px solid"
                              : "none",
                          borderColor: isHighlighted
                            ? "rgba(46, 125, 50, 0.7)"
                            : isSelected
                              ? "primary.main"
                              : "divider",
                          transform: isSelected ? "scale(1.01)" : "none",
                          borderRadius: 0,
                          overflow: "hidden",
                          transition: "box-shadow 0.3s, transform 0.3s",
                          aspectRatio: "1",
                          "&:hover": {
                            transform: "scale(1.01)",
                          },
                        }}
                        onClick={() => onFolderClick(imageFile)}
                      >
                        {renderImageThumbnail(imageFile)}
                      </Card>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  if (folderLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load folder details: {error}
      </Alert>
    );
  }

  if (!currentFolder) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No folder data available.
      </Alert>
    );
  }

  return renderFolderContent();
};

export default DrawerContent;
